'use client';

import { sendGTMEvent } from '@next/third-parties/google';

import { toAbsoluteUrl } from '@/util/to-absolute-url';
import { parseHrefSearchParam } from '@/util/url-search-page';

import { getPageCategory } from '../utils/getPageCategory';
import { CartEcommerceItem, mapCartGTMItem } from '../utils/mapCartGTMItem';
import { sanitizeGTMString } from '../utils/sanitizeGTMString';

import {
  GTMAuthContext,
  GTMAuthMethod,
  GTMAuthOpenLabel,
  GTMEventAuth,
  GTMEventAuthAlternativeSwitch,
  GTMEventAuthCancel,
  GTMEventAuthCodeSent,
  GTMEventAuthCodeSubmit,
  GTMEventAuthOpen,
  GTMEventAuthStart,
  GTMEventEcommerce,
  GTMEcommerceItem,
  GTMEventNameEcommerce,
  GTMItemListName,
  GTMEventPromotion
} from './types';

const STORAGE_KEY = 'gtm:item-list-names';
const FALLBACK_ITEM_LIST_NAME: GTMItemListName = 'cart';

const GTM_EVENTS_WITH_VALUE = new Set<GTMEventNameEcommerce>([
  'view_item',
  'add_to_cart',
  'remove_from_cart',
  'add_to_wishlist',
  'remove_from_wishlist',
  'view_cart',
  'begin_checkout',
  'add_payment_info',
  'add_shipping_info',
  'purchase'
]);

const getStoreItemListName = (): Record<string, GTMItemListName> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const setStoreItemListName = (store: Record<string, GTMItemListName>) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    //
  }
};

export const clearStoreItemListName = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    //
  }
};

export const sendGTMEventEcommerce = <T extends GTMEventNameEcommerce>(
  data: GTMEventEcommerce<T>
) => {
  const store = getStoreItemListName();
  let isChanged = false;

  const items: GTMEcommerceItem[] = data.ecommerce.items.map((item) => {
    if (data.event === 'view_item_list' && item.item_list_name) {
      // view_item_list всегда перезаписывает лист в хранилище
      store[item.item_id] = item.item_list_name;
      isChanged = true;
    } else if (
      data.event === 'view_item' &&
      item.item_list_name &&
      !store[item.item_id]
    ) {
      // view_item сохраняет лист, только если его ещё нет
      store[item.item_id] = item.item_list_name;
      isChanged = true;
    }

    return {
      ...item,
      item_list_name:
        store[item.item_id] || item.item_list_name || FALLBACK_ITEM_LIST_NAME
    };
  });

  if (isChanged) setStoreItemListName(store);

  const value =
    data.ecommerce.value ??
    (GTM_EVENTS_WITH_VALUE.has(data.event)
      ? items.reduce((sum, item) => sum + item.price * (item.quantity ?? 1), 0)
      : undefined);

  sendGTMEvent({ ecommerce: null });

  sendGTMEvent({
    event: data.event,
    ...(data.discount_source !== undefined && {
      discount_source: data.discount_source
    }),
    ecommerce: {
      currency: data.ecommerce.currency || 'RUB',
      items,
      ...(value !== undefined && { value }),
      ...(data.ecommerce.id && { id: data.ecommerce.id }),
      ...(data.ecommerce.coupon && { coupon: data.ecommerce.coupon }),
      ...(data.ecommerce.payment_type && {
        payment_type: data.ecommerce.payment_type
      }),
      ...(data.ecommerce.shipping_tier && {
        shipping_tier: data.ecommerce.shipping_tier
      })
    }
  });
};

export const sendGTMEventAuth = (data: GTMEventAuth) => {
  sendGTMEvent(data);
};

export const mapAuthDeliveryToGTMMethod = (
  deliveryType: 'sms' | 'call'
): GTMAuthMethod => (deliveryType === 'call' ? 'flashing_call' : 'sms');

export const sendGTMEventAuthOpen = (payload: {
  event_label: GTMAuthOpenLabel;
  auth_context: GTMAuthContext;
}) => {
  const data: GTMEventAuthOpen = {
    event: 'auth_open',
    event_category: 'authorization',
    event_action: 'open',
    event_label: payload.event_label,
    auth_context: payload.auth_context
  };
  sendGTMEvent(data);
};

export const sendGTMEventAuthStart = (payload: {
  auth_session_id: string;
  auth_method: GTMAuthMethod;
  auth_context: GTMAuthContext;
}) => {
  const data: GTMEventAuthStart = {
    event: 'auth_start',
    event_category: 'authorization',
    event_action: 'start',
    event_label: payload.auth_method,
    auth_session_id: payload.auth_session_id,
    auth_method: payload.auth_method,
    auth_context: payload.auth_context
  };
  sendGTMEvent(data);
};

export const sendGTMEventAuthCodeSent = (payload: {
  auth_session_id: string;
  auth_method: GTMAuthMethod;
  provider?: GTMAuthMethod;
}) => {
  const data: GTMEventAuthCodeSent = {
    event: 'auth_code_sent',
    event_category: 'authorization',
    event_action: 'code sent',
    event_label: payload.auth_method,
    auth_session_id: payload.auth_session_id,
    auth_method: payload.auth_method,
    provider: payload.provider ?? payload.auth_method
  };
  sendGTMEvent(data);
};

export const sendGTMEventAuthCodeSubmit = (payload: {
  auth_session_id: string;
  auth_method: GTMAuthMethod;
  attempt_number: number;
}) => {
  const data: GTMEventAuthCodeSubmit = {
    event: 'auth_code_submit',
    event_category: 'authorization',
    event_action: 'code submit',
    event_label: payload.auth_method,
    auth_session_id: payload.auth_session_id,
    auth_method: payload.auth_method,
    attempt_number: payload.attempt_number
  };
  sendGTMEvent(data);
};

export const sendGTMEventAuthCancel = (payload: {
  auth_session_id: string;
  auth_context: GTMAuthContext;
}) => {
  const data: GTMEventAuthCancel = {
    event: 'auth_cancel',
    event_category: 'authorization',
    event_action: 'cancel',
    event_label: payload.auth_context,
    auth_session_id: payload.auth_session_id,
    auth_context: payload.auth_context
  };
  sendGTMEvent(data);
};

export const sendGTMEventAuthAlternativeSwitch = (payload: {
  auth_session_id: string;
  auth_context: GTMAuthContext;
}) => {
  const data: GTMEventAuthAlternativeSwitch = {
    event: 'auth_alternative_switch',
    event_category: 'authorization',
    event_action: 'alternative_switch',
    event_label: 'flashing_call_to_sms',
    auth_session_id: payload.auth_session_id,
    from_method: 'flashing_call',
    to_method: 'sms',
    auth_context: payload.auth_context
  };
  sendGTMEvent(data);
};

export const sendGTMEventPromotion = (data: GTMEventPromotion) => {
  sendGTMEvent(data);
};

type GTMMenuClickAction = 'header' | 'footer' | 'burger menu' | 'logo';

export type GTMInternalSearchMethod =
  | 'manual'
  | 'autocompletion::history'
  | 'autocompletion::frequent search'
  | 'autocompletion::search in category';

export const sendGTMEventInternalSearch = (
  method: GTMInternalSearchMethod,
  searchKeyword: string | null | undefined
) => {
  const eventLabel = sanitizeGTMString(searchKeyword);
  if (!eventLabel) return;

  sendGTMEvent({
    event: 'internal_search',
    event_category: 'internal search',
    event_action: method,
    event_label: eventLabel
  });
};

export type GTMUseFiltersCategory = 'catalog' | 'menu' | 'online';
export type GTMUseFiltersAction = 'filter' | 'sort';

export const sendGTMEventUseFilters = (
  category: GTMUseFiltersCategory,
  action: GTMUseFiltersAction,
  filterName: string | null | undefined,
  filterValue: string | null | undefined
): boolean => {
  if (action === 'sort') {
    const value = sanitizeGTMString(filterValue);
    if (!value) return false;

    sendGTMEvent({
      event: 'use_filters',
      event_category: category,
      event_action: action,
      event_label: value
    });
    return true;
  }

  const name = sanitizeGTMString(filterName);
  if (!name) return false;

  const value = sanitizeGTMString(filterValue);

  sendGTMEvent({
    event: 'use_filters',
    event_category: category,
    event_action: action,
    event_label: value ? `${name}::${value}` : name
  });
  return true;
};

export const sendGTMEventMenuFilter = (
  href: string,
  filterName: string | null | undefined,
  filterValue: string | null | undefined,
  labelPrefix?: string | null,
  sortLabel?: string | null
): boolean => {
  let sent = false;

  if (sortLabel) {
    sent = sendGTMEventUseFilters('menu', 'sort', null, sortLabel);
  }

  if (parseHrefSearchParam(href, 'f') === null) return sent;

  const value = filterValue?.trim();

  const name =
    filterName?.trim() ||
    labelPrefix
      ?.split('::')
      .map((part) => part.trim())
      .filter(Boolean)
      .at(-1) ||
    value;

  return sendGTMEventUseFilters('menu', 'filter', name, value) || sent;
};

export type GTMNewsletterRegistrationLabel = 'footer' | 'pop-up';

export const sendGTMEventNewsletterRegistration = (
  label: GTMNewsletterRegistrationLabel
) => {
  sendGTMEvent({
    event: 'newsletter_registration',
    event_category: 'registration',
    event_action: 'newsletter',
    event_label: label
  });
};

export const sendGTMEventAddContactInfo = () => {
  sendGTMEvent({
    event: 'add_contact_info',
    event_category: 'registration',
    event_action: 'add info',
    event_label: 'account registration'
  });
};

export type GTMApplyGiftCardAction = 'personal account' | 'gift card';

export const sendGTMEventApplyGiftCard = (
  action: GTMApplyGiftCardAction,
  orderCost: number
) => {
  sendGTMEvent({
    event: 'apply_gift_card',
    event_category: 'use gift card',
    event_action: action,
    event_label: orderCost <= 0 ? 'full payment' : 'partial payment'
  });
};

export const sendGTMEventScrollGallery = (
  mediaType: 'photo' | 'video',
  productName: string | null | undefined
) => {
  const eventLabel = sanitizeGTMString(productName);
  if (!eventLabel) return;

  sendGTMEvent({
    event: 'scroll_gallery',
    event_category: 'product gallery',
    event_action: mediaType,
    event_label: eventLabel
  });
};

export const sendGTMEventReadReview = (
  pathname: string,
  productName: string | null | undefined
) => {
  const eventLabel = sanitizeGTMString(productName);
  if (!eventLabel) return;

  sendGTMEvent({
    event: 'read_review',
    event_category: getPageCategory(pathname),
    event_action: 'read review',
    event_label: eventLabel
  });
};

export const sendGTMEventWriteReview = (
  pathname: string,
  productName: string | null | undefined
) => {
  const eventLabel = sanitizeGTMString(productName);
  if (!eventLabel) return;

  sendGTMEvent({
    event: 'write_review',
    event_category: getPageCategory(pathname),
    event_action: 'write review',
    event_label: eventLabel
  });
};

export const sendGTMEventSubmitReview = (
  pathname: string,
  productName: string | null | undefined
) => {
  const eventLabel = sanitizeGTMString(productName);
  if (!eventLabel) return;

  sendGTMEvent({
    event: 'submit_review',
    event_category: getPageCategory(pathname),
    event_action: 'submit review',
    event_label: eventLabel
  });
};

export type GTMBodyButtonClickParams = {
  action: string;
  label: string;
  url?: string | null;
};

export const sendGTMEventBodyButtonClick = (
  pathname: string,
  { action, label, url }: GTMBodyButtonClickParams
) => {
  const eventAction = sanitizeGTMString(action);
  const buttonLabel = sanitizeGTMString(label);
  if (!eventAction || !buttonLabel) return;

  const urlValue = url?.trim();
  const eventLabel = urlValue
    ? `${buttonLabel}::${toAbsoluteUrl(urlValue)}`
    : `${buttonLabel}::none`;

  sendGTMEvent({
    event: 'body_button_click',
    event_category: getPageCategory(pathname),
    event_action: eventAction,
    event_label: eventLabel
  });
};

export const sendGTMEventSocialInteraction = (
  platform: string | null | undefined,
  url: string | null | undefined
) => {
  const eventCategory = platform?.trim();
  const eventLabel = url?.trim();
  if (!eventCategory || !eventLabel) return;

  const absoluteUrl =
    typeof window === 'undefined'
      ? eventLabel
      : new URL(eventLabel, window.location.origin).toString();

  sendGTMEvent({
    event: 'social_interaction',
    event_category: eventCategory,
    event_action: 'click',
    event_label: absoluteUrl
  });
};

export const sendGTMEventMenuClick = (
  action: GTMMenuClickAction,
  label: string | null | undefined
) => {
  const eventLabel = label?.trim();
  if (!eventLabel) return;

  sendGTMEvent({
    event: 'menu_click',
    event_category: 'main menu navigation',
    event_action: action,
    event_label: eventLabel
  });
};

export const sendGTMEventStoriesClick = (
  index: number,
  storiesName: string | null | undefined,
  storiesId: number
) => {
  const name = storiesName?.trim();
  if (!name) return;

  sendGTMEvent({
    event: 'stories_click',
    event_category: 'stories',
    event_action: index,
    event_label: name,
    stories_id: storiesId,
    stories_name: name
  });
};

export const sendGTMEventStoriesView = (
  storyIndex: number,
  slideIndex: number,
  storiesName: string | null | undefined,
  slideName: string | null | undefined,
  storiesId: number
) => {
  const name = storiesName?.trim();
  const slide = slideName?.trim();
  if (!name || !slide) return;

  sendGTMEvent({
    event: 'view_stories',
    event_category: storyIndex,
    event_action: slideIndex,
    event_label: `${name}::${slide}`,
    stories_id: storiesId,
    stories_name: name,
    slide_name: slide
  });
};

export const sendGTMEventStoriesButtonClick = (
  storyIndex: number,
  slideIndex: number,
  storiesName: string | null | undefined,
  slideName: string | null | undefined,
  ctaLabel: string | null | undefined,
  ctaUrl: string | null | undefined,
  storiesId: number
) => {
  const name = storiesName?.trim();
  const slide = slideName?.trim();
  const label = ctaLabel?.trim();
  const url = ctaUrl?.trim();
  if (!name || !slide || !label || !url) return;

  const absoluteUrl =
    typeof window === 'undefined' ? url : new URL(url, window.location.origin).toString();

  sendGTMEvent({
    event: 'stories_button_click',
    event_category: storyIndex,
    event_action: slideIndex,
    event_label: `${name}::${slide}`,
    cta_name: `${label}::${absoluteUrl}`,
    stories_id: storiesId,
    stories_name: name,
    slide_name: slide
  });
};

export function sendEcommerceRemoveFromCartEvent(items: CartEcommerceItem[]) {
  if (items.length === 0) return;

  sendGTMEventEcommerce({
    event: 'remove_from_cart',
    ecommerce: {
      items: items.map(mapCartGTMItem)
    }
  });
}

export function sendEcommerceAddToCartEvent(items: CartEcommerceItem[]) {
  if (items.length === 0) return;

  sendGTMEventEcommerce({
    event: 'add_to_cart',
    ecommerce: {
      items: items.map(mapCartGTMItem)
    }
  });
}

export { sendGTMEvent };
